import { zonedDate } from '../../../../utils/date-time';
import moment from 'moment';
import ScheduleStatus from '../components/ScheduleStatus';
import { Agendamento, StatusHorario } from '../../Enum';
import { formatBRL } from '../../cliente/List/helpers';
import { baseURL } from '../../../../AxiosConfig';
import { Modal } from '../../../../components/Modal';
import ClientesForm from '../../../clientes/Form';
import React, { useEffect, useState } from 'react';
import ClientGallery from '../../../clientes/view/gallery/ClientGallery';
import { Images, MessageCircle, TriangleAlert, UserRound } from 'lucide-react';

export interface ScheduleViewHeaderProps {
    schedule: Agendamento;
    onEditToggle: () => void;
}

export default function ScheduleViewHeader({ schedule, onEditToggle }: ScheduleViewHeaderProps) {
    const [profileImageUrl, setProfileImageUrl] = useState<string>(`${baseURL}/users/profileImage/${schedule.client.uid}`);
    const start = zonedDate(schedule.startAt, schedule.timezone);
    const end = zonedDate(schedule.endAt, schedule.timezone);
    const [openEditClient, setOpenEditClient] = useState(false);
    const [openGalleryClient, setOpenGalleryClient] = useState(false);
    const scheduleCount = Number(schedule.client.amountSchedules) || 0;
    const durationMinutes = Math.max(end.diff(start, 'minutes'), 0);
    const extrasCount = schedule.servicosExtras?.length || 0;
    // Ano so aparece fora do ano corrente, como no Calendario do iOS.
    const dateFormat = start.year() === moment().year() ? 'dddd, D [de] MMMM' : 'dddd, D [de] MMMM [de] YYYY';
    const phoneDigits = schedule.client.phoneNumber?.replace(/\D/g, '') || '';

    useEffect(() => {
        setProfileImageUrl(
            schedule.client.profileImageUrl || `${baseURL}/users/profileImage/${schedule.client.uid}`
        );
    }, [schedule.client.profileImageUrl, schedule.client.uid]);

    return (
        <div className="sf-detail-header">
            {openEditClient && (
                <Modal onClose={() => setOpenEditClient(false)} title="Editar Cliente" panelClassName="sf-schedule-detail-panel">
                    <ClientesForm
                        doc={{
                            uid: schedule.client.uid,
                            displayName: schedule.client.name,
                            email: schedule.client.email,
                            photoURL: schedule.client.profileImageUrl ?? '',
                            phoneNumber: schedule.client.phoneNumber ?? '',
                            disabled: false,
                            type: 'client',
                            emailVerified: true,
                            gruposIds: [''],
                        }}
                        cancel={() => {
                            setOpenEditClient(false);
                        }}
                        callback={(update) => {
                            schedule.client.name = update.displayName || '';
                            schedule.client.phoneNumber = update.phoneNumber;
                            schedule.client.profileImageUrl = update.photoURL;
                            setProfileImageUrl(
                                update.photoURL || `${baseURL}/users/profileImage/${schedule.client.uid}`
                            );
                            setOpenEditClient(false);
                        }}
                    />
                </Modal>
            )}

            {openGalleryClient && (
                <Modal onClose={() => setOpenGalleryClient(false)} title="Cliente" panelClassName="sf-schedule-detail-panel">
                    <ClientGallery clientId={schedule.client.uid} />
                </Modal>
            )}

            <section className="sf-detail-hero" aria-label="Horário do atendimento">
                <p className="sf-detail-date">{start.format(dateFormat)}</p>
                <div className="sf-detail-time">
                    {start.format('HH:mm')} <span>–</span> {end.format('HH:mm')}
                </div>
                <p className="sf-detail-service">
                    <span className="truncate">{schedule.service?.name}</span>
                    {extrasCount > 0 && <span className="sf-detail-service-meta">+{extrasCount} extra{extrasCount > 1 ? 's' : ''}</span>}
                    {durationMinutes > 0 && <span className="sf-detail-service-meta">· {durationMinutes} min</span>}
                </p>
                <div className="sf-detail-appointment-actions">
                    <ScheduleStatus
                        clientId={schedule.client.uid}
                        key={schedule._id}
                        status={schedule.status}
                        id={schedule._id}
                        amount={schedule.subscriptionLimit?.expectedTotal ?? schedule.totalPrice ?? schedule.service?.price ?? 0}
                        paymentMethod={schedule.paymentMethod}
                        paymentStatus={schedule.paymentStatus}
                        paymentProvider={schedule.paymentProvider}
                    />
                    <button type="button" onClick={onEditToggle} className="sf-detail-link" title="Editar agendamento">
                        Editar
                    </button>
                </div>
                {schedule.subscriptionLimit?.reached && schedule.status !== StatusHorario.CONCLUIDO && (
                    <div className="alert alert-warning mt-3 text-left text-sm">
                        <TriangleAlert aria-hidden />
                        <span>
                            O limite de usos do plano foi atingido. Ao concluir, o valor cobrado será{' '}
                            {formatBRL(schedule.subscriptionLimit.expectedTotal)}.
                        </span>
                    </div>
                )}
            </section>

            <section className="sf-detail-group sf-detail-client" aria-label="Cliente">
                <div className="sf-detail-client-row">
                    <div className="sf-detail-client-avatar">
                        <img src={profileImageUrl} alt="" className="h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="sf-detail-client-name">{schedule.client.name}</h3>
                        <div className="sf-detail-contact">
                            {schedule.client.phoneNumber && <span>{schedule.client.phoneNumber}</span>}
                            {schedule.client.email && <span>{schedule.client.email}</span>}
                        </div>
                    </div>
                    <span className="sf-detail-count">
                        {scheduleCount > 1 ? `${scheduleCount} visitas` : scheduleCount === 1 ? 'Primeira visita' : 'Novo'}
                    </span>
                </div>

                {/* Acoes rapidas no estilo do app Contatos: icone sobre o rotulo, em botoes do mesmo tamanho. */}
                <div className="sf-detail-quick-actions">
                    {phoneDigits ? (
                        <a href={`https://wa.me/${phoneDigits}`} target="_blank" rel="noopener noreferrer"
                            className="sf-detail-quick-action is-whatsapp">
                            <MessageCircle aria-hidden="true" />
                            <span>WhatsApp</span>
                        </a>
                    ) : (
                        <span className="sf-detail-quick-action" aria-disabled="true" title="Cliente sem telefone">
                            <MessageCircle aria-hidden="true" />
                            <span>WhatsApp</span>
                        </span>
                    )}
                    <button type="button" onClick={() => setOpenEditClient(true)} className="sf-detail-quick-action">
                        <UserRound aria-hidden="true" />
                        <span>Editar cliente</span>
                    </button>
                    <button type="button" onClick={() => setOpenGalleryClient(true)} className="sf-detail-quick-action">
                        <Images aria-hidden="true" />
                        <span>Galeria</span>
                    </button>
                </div>
            </section>
        </div>
    );
}
